<template>
  <AppDialogContent
    v-model="dialogValue"
    :loading="formSubmitted"
    :persistent="formSubmitted"
    :disabled-submit="!beddingType"
    custom-header
    @submit-form="submit"
  >
    <template #header>
      <div class="d-flex justify-space-between py-4">
        <div v-if="!!responseType" style="align-self: center">
          <div class="text-h5 font-weight-bold">
            {{ responseTexts.headline }}
          </div>
        </div>
        <div v-else style="align-self: center">
          <div class="text-h5 font-weight-bold mb-2">
            {{
              $t(
                "events:entryAction.beddingOnHorse.heading",
                "Specify bedding for horse"
              )
            }}
          </div>
        </div>
        <div class="float-end">
          <v-btn-icon
            icon
            variant="text"
            :disabled="formSubmitted"
            class="mx-0"
            data-test="closeDialog"
            :aria-label="$t('common:closeButton.alt')"
            @click="dialogValue = false"
          >
            <v-icon>$iconTimesCircle</v-icon>
          </v-btn-icon>
        </div>
      </div>
    </template>

    <template #default>
      <div v-if="!!responseType">
        <EntryFeiResponse
          :response-type="responseType"
          :mutation-errors="mutationErrors"
        >
          <template #default>
            {{ responseTexts.main }}
          </template>
          <template #reason>
            <div v-if="responseTexts.reason">
              {{ responseTexts.reason }}<br />
            </div>
          </template>
          <template #additional-message>
            {{ responseTexts.additional }}
          </template>
        </EntryFeiResponse>
      </div>
      <div v-else-if="entryLoading">
        <v-progress-linear indeterminate />
      </div>
      <div v-else-if="entry">
        <AppEntryRegistrationOverview v-bind="{ registration, competition }">
          <template #competitionCodeheader>
            {{
              $t("events:entryAction.beddingOnHorse.competition.label", "Event")
            }}
          </template>
          <template v-if="mergedHorses.length == 1" #horseNamesHeader>
            {{
              $t("events:entryAction.beddingOnHorse.horseNames.label", "Horse")
            }}
          </template>
        </AppEntryRegistrationOverview>

        <br />

        <v-select
          v-if="mergedHorses.length > 1"
          v-model="selectHorse"
          return-object
          item-title="name"
          :label="
            $t('events:entryAction.beddingOnHorse.horseNames.label', 'Horse')
          "
          variant="outlined"
          data-test="selectHorseForBeddingType"
        />

        <v-radio-group v-if="!!selectedHorse" v-model="beddingType">
          <template #label>
            <span style="font-size: 16px"
              ><strong>{{
                $t(
                  "events:entryAction.beddingOnHorse.beddingType.heading",
                  "Bedding type"
                )
              }}</strong></span
            >
          </template>
          <v-radio
            v-for="bedType in beddingTypes"
            :key="bedType.value"
            :value="bedType.value"
            :label="bedType.text"
          />
        </v-radio-group>
      </div>
    </template>

    <template #submit>
      {{
        $t("events:entryAction.beddingOnHorse.setBedding.button", "Set bedding")
      }}
    </template>

    <template v-if="!!responseType" #actions>
      <v-row dense style="justify-content: flex-end">
        <div class="mb-1 text-center text-md-right">
          <v-btn
            rounded
            variant="outlined"
            min-width="180px"
            data-test="dialogClose"
            @click="dialogValue = false"
          >
            {{ responseTexts.okBtn }}
          </v-btn>
        </div>
      </v-row>
    </template>
  </AppDialogContent>
</template>
<script>
export const BeddingTypes = ["SHAVINGS", "STRAW", "EMPTY"];
</script>
<script setup>
import { ref, computed } from "vue";
import gql from "fraql";
import { apolloClient } from "@/plugins/apollo";
import AppDialogContent from "@elements/AppDialogContent.vue";
import EntryFeiResponse from "./EntryFeiResponse.vue";
import AppEntryRegistrationOverview from "@elements/AppEntryRegistrationOverview.vue";
import HorseRegistrationMerge, {
  RegistrationHorseFragment
} from "@composables/HorseRegistrationMerge.ts";
import { useEntryFeiActionsInjections } from "./compositions";

import allowedMutation from "@/utils/allowedMutation";
import { injectSnackbarError } from "@/composables/injections";
import { useTranslation } from "i18next-vue";

defineOptions({
  name: "FeiHorseBeddingType"
});

const { fragments, refetchQueries } = useEntryFeiActionsInjections();
const { snackbarServerError } = injectSnackbarError();
const { t } = useTranslation();

const selectedHorse = ref(null);
const beddingType = ref(null);
const modal = ref(false);
const formSubmitted = ref(false);
const responseType = ref(null);
const feedbackMessages = ref([]);
const mutationErrors = ref([]);

const entryLoading = ref(false);
const entry = ref(null);

const dialogValue = computed({
  get() {
    return modal.value;
  },
  set(v) {
    if (v == true && !modal.value) {
      responseType.value = null;
      feedbackMessages.value = [];
      mutationErrors.value = [];

      selectedHorse.value = null;
      beddingType.value = null;
    }

    modal.value = v;
  }
});

const registration = computed(() => {
  return entry.value?.registration ?? { horses: [], registrationHorses: [] };
});
const competition = computed(() => {
  return entry.value?.competition;
});

const mergedHorses = computed(() => {
  return HorseRegistrationMerge(
    registration.value.horses,
    registration.value.registrationHorses
  ).filter(horse => {
    const regHorse = registration.value.registrationHorses.find(
      regHorse => regHorse.horseId == horse.id
    );
    return allowedMutation(
      regHorse?.allowedMutations ?? [],
      "registrationHorseUpdateBeddingType"
    );
  });
});

const selectHorse = computed({
  get() {
    return selectedHorse.value;
  },
  set(v) {
    selectedHorse.value = v;
    beddingType.value = v.beddingType;
  }
});
const beddingTypes = computed(() => {
  return [
    {
      text: t(
        "events:entryAction.beddingOnHorse.beddingType.SHAVINGS.label",
        "Shavings"
      ),
      value: "SHAVINGS"
    },
    {
      text: t(
        "events:entryAction.beddingOnHorse.beddingType.STRAW.label",
        "Straw"
      ),
      value: "STRAW"
    },
    {
      text: t(
        "events:entryAction.beddingOnHorse.beddingType.EMPTY.label",
        "Empty (no preference)"
      ),
      value: "EMPTY"
    }
  ];
});
const responseTexts = computed(() => {
  switch (responseType.value) {
    case "error":
      return {
        headline: t(
          "events:entryAction.beddingOnHorse.failed.heading",
          "Specify bedding type"
        ),
        main: t(
          "events:entryAction.beddingOnHorse.failed.mainMessage.text",
          "Adding or changing bedding failed"
        ),
        reason: t(
          "events:entryAction.selectHorseForSlot.failed.reason.text",
          "The FEI entry system reported the following reason:"
        ),
        additional: t(
          "events:entryAction.beddingOnHorse.failed.additionalMessage.text",
          "Please contact your National Federation, if you need help on this matter."
        ),
        okBtn: t("events:entryAction.beddingOnHorse.failed.ok.button", "OK")
      };
    default:
      return null;
  }
});

async function openFeiActionForm({ entry: e }) {
  entryLoading.value = true;
  dialogValue.value = true;
  try {
    const { data } = await apolloClient.query({
      query: gql`
        query FeiHorseBeddingTypeFetch($id: ID!) {
          entry(id: $id) {
            id
            connectedWithFei
            allowedMutations
            registration {
              id
              registrationHorses {
                id
                horseId
                beddingType
                ${RegistrationHorseFragment}
                allowedMutations
              }
              horses {
                id
                ${AppEntryRegistrationOverview.fragments.horseFields}
              }
            }
            competition {
              id
              code
              ${AppEntryRegistrationOverview.fragments.competitionFields}
            }
          }
        }
      `,
      variables: {
        id: e.id
      },
      fetchPolicy: "network-only"
    });
    entry.value = data.entry;

    selectedHorse.value = mergedHorses.value[0];
    beddingType.value = selectedHorse.value.beddingType;
  } catch (error) {
    snackbarServerError(error, { level: "error" });
  } finally {
    entryLoading.value = false;
  }
}

async function submit() {
  if (!beddingType.value) return;
  formSubmitted.value = true;

  try {
    const { errors } = await apolloClient.mutate({
      mutation: gql`
        mutation registrationHorseUpdateBeddingType(
          $id: ID!
          $beddingType: String
        ) {
          registrationHorseUpdateBeddingType(
            input: { id: $id, beddingType: $beddingType }
          ) {
            registrationHorse {
              id
              horseId
              beddingType
              allowedMutations
            }
          }
        }
      `,
      variables: {
        id: selectedHorse.value.registrationHorseId,
        beddingType: beddingType.value
      }
    });

    if (errors?.length) {
      responseType.value = "error";
      mutationErrors.value = errors;
    } else {
      dialogValue.value = false;
    }
  } catch (error) {
    responseType.value = "error";
    if (error?.graphQLErrors) {
      mutationErrors.value = error.graphQLErrors;
    }
  } finally {
    formSubmitted.value = false;
  }
}

defineExpose({ openFeiActionForm });
</script>
